import { FragmentType, gql, useFragment } from '@/__generated__';
import { Button } from '@/components/shadcn/button';
import { Dialog, DialogContent } from '@/components/shadcn/dialog';
import { useToast } from '@/components/shadcn/use-toast';
import React, { useState } from 'react';
import { ColorResult, SketchPicker } from 'react-color';

const BrandColorsFragment = gql(`
  fragment BrandColorsFragment on Organization {
    brandColors
  }
`);

interface ColorListProps {
  brandColors?: FragmentType<typeof BrandColorsFragment>;
  onUpdateColors: (colors: string[]) => void;
}

const MAX_COLOR_PICK = 6;

const ColorList = ({ brandColors, onUpdateColors }: ColorListProps) => {
  const colorsFragment = useFragment(BrandColorsFragment, brandColors);
  const [selectedColorIndex, setSelectedColorIndex] = useState<number>();
  const [open, setOpen] = useState(false);
  const [colors, setColors] = useState<string[]>(
    colorsFragment?.brandColors || []
  );
  const { toast } = useToast();
  const handleColorChange = (index: number, color: ColorResult) => {
    const newColors = [...(colors as string[])];
    newColors[index] = color.hex;

    setColors(newColors);
    onUpdateColors(newColors);
  };

  const addColor = () => {
    if (colors?.length === MAX_COLOR_PICK) {
      toast({
        description: `You've reached max colors size`,
        duration: 3000,
        variant: 'destructive',
      });
      return;
    }
    const newColors = colors ? [...colors] : [];

    setColors((prevState: string[]) => [...prevState, '#FFF']);
    onUpdateColors(newColors);
  };

  const removeColor = (index: number) => {
    const newColors = [...(colors as string[])];
    newColors.splice(index, 1);
    setColors(newColors);
    onUpdateColors(newColors);
  };

  const handleColorBoxClick = (index: number) => {
    setSelectedColorIndex(index);
    setOpen(true);
  };

  return (
    <div>
      <p className='mb-2 text-base font-medium'>Colors</p>
      <Button onClick={addColor} variant={'maggie-secondary'}>
        + Add Color
      </Button>
      <div className='mt-6 flex gap-4'>
        {colors?.map((color: string, index: number) => (
          <div key={index}>
            <div
              className={`h-12 w-12 cursor-pointer rounded-lg border border-border`}
              style={{ background: color }}
              onClick={() => handleColorBoxClick(index)}
            />
            <Dialog
              open={open && selectedColorIndex === index}
              onOpenChange={setOpen}
            >
              <DialogContent className='max-w-[270px] pt-12'>
                <SketchPicker
                  disableAlpha={true}
                  color={color}
                  onChange={(newColor) => handleColorChange(index, newColor)}
                />
              </DialogContent>
            </Dialog>
            <p
              className='cursor-pointer text-center text-xs text-maggie-500'
              onClick={() => removeColor(index)}
            >
              Remove
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ColorList;
